import { EyeOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { CollectionProductItem } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { StoreProductStatusBadge } from '@/features/store-catalog';

export interface CollectionProductRowProps {
  item: CollectionProductItem;
  /** Controls rendered at the end of the row (reorder, remove). */
  actions?: ReactNode;
  className?: string;
}

/** One product of a collection: position, title, handle, store status, source. Unpublished rows are flagged. */
export function CollectionProductRow({ item, actions, className }: CollectionProductRowProps) {
  const { t } = useTranslation('collections');
  const hidden = item.status !== 'published';
  return (
    <li className={cn('flex items-center gap-3 px-3 py-2 text-sm', className)}>
      <span className="text-muted-foreground w-6 shrink-0 text-right tabular-nums">
        {item.position + 1}
      </span>
      <div className="min-w-0 flex-1">
        <span className="block truncate font-medium">{item.title}</span>
        <span className="text-muted-foreground block truncate font-mono text-[11px]">
          {item.handle}
        </span>
      </div>
      {hidden ? (
        <span className="text-warning-foreground dark:text-warning hidden items-center gap-1 text-xs sm:inline-flex">
          <EyeOff className="size-3.5" aria-hidden />
          {t('products.notVisible')}
        </span>
      ) : null}
      <StoreProductStatusBadge status={item.status} />
      <Badge variant="outline" className="text-muted-foreground hidden sm:inline-flex">
        {t(`products.source.${item.source}`)}
      </Badge>
      {actions}
    </li>
  );
}
